import { useState } from 'react';
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import { styled } from '@mui/material/styles';
import { useTheme, Stack, IconButton, Typography } from "@mui/material";
import FastRewindIcon from '@mui/icons-material/FastRewind';
import FastForwardIcon from '@mui/icons-material/FastForward';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey.C050,
    color: theme.palette.common.black,
    fontSize: '14px',
    fontWeight: '400',
    paddingBottom: '1rem',
    border: '1px solid',
  },
  [`&.${tableCellClasses.body}`]: {
    color: theme.palette.common.black,
    fontSize: '12px',
    padding: theme.spacing(1),
    fontWeight: '400',
    border: '1px solid',
  },
}));

const StyledTableCellDark = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey.C050,
    color: theme.palette.common.white,
    fontSize: '18px',
    fontWeight: '400',
    paddingBottom: '1rem',
    border: '1px solid',
  },
  [`&.${tableCellClasses.body}`]: {
    color: theme.palette.common.white,
    fontSize: '12px',
    padding: theme.spacing(1),
    fontWeight: '400',
    border: '1px solid',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.grey.C050 },
  '&:last-child td, &:last-child th': { order: 0 },
}));

const StyledTableRowDark = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.grey.C050,
    color: theme.palette.common.white,
  },
  '&:last-child td, &:last-child th': { order: 0 },
}));

export default function TableCard({ columns, data }) {
  const theme = useTheme();
  const isDark = theme.palette.mode !== 'light';

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    autoResetPageIndex: true,
  });

  const table = useReactTable({
    data,
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const Cell = isDark ? StyledTableCellDark : StyledTableCell;
  const Row  = isDark ? StyledTableRowDark  : StyledTableRow;

  return (
    <>
      <TableContainer sx={{ bgcolor: isDark ? 'greydark.C400' : 'grey.C050', mt: '1rem', p: isDark ? 0 : '2rem' }}>
        <Table size="small">
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Cell key={header.id} colSpan={header.colSpan} align="center">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </Cell>
                ))}
              </Row>
            ))}
          </TableHead>

          <TableBody>
            {/* FIX: was getCoreRowModel().rows — pagination had no effect.
                getRowModel().rows respects the current page slice. */}
            {table.getRowModel().rows.map((row) => (
              <Row key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Cell key={cell.id} align="center">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Cell>
                ))}
              </Row>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{justifyContent: 'center', mt: '1rem', flexWrap: 'wrap', bgcolor: 'grey.C100', borderRadius:7 }}>
        <IconButton size="large" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
          <FastRewindIcon fontSize="inherit" />
        </IconButton>
        <IconButton size="large" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
          <ArrowLeftIcon fontSize="inherit" />
        </IconButton>
        <IconButton size="large" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
          <ArrowRightIcon fontSize="inherit" />
        </IconButton>
        <IconButton size="large" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
          <FastForwardIcon fontSize="inherit" />
        </IconButton>

        <Typography variant="body2">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </Typography>

        <Typography variant="body2" sx={{ ml: 2 }}>
          Showing {table.getRowModel().rows.length.toLocaleString()} of {table.getRowCount().toLocaleString()} rows
        </Typography>

        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
          style={{ marginLeft: '1rem' }}
        >
          {[10, 20, 30, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>Show {pageSize}</option>
          ))}
        </select>
      </Stack>
    </>
  );
}